import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { getEmailByUsername, getUserIdByUsername, trimUsername } from '../lib/usernames';
import './AuthScreen.css';

const AuthScreen = ({ onAuth, onClose }) => {
  const [mode, setMode] = useState('signup'); // 'signup' | 'signin'
  const [email, setEmail] = useState('');     // sign-up email
  const [loginId, setLoginId] = useState(''); // sign-in: email or username
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    const trimmedUsername = trimUsername(username);

    if (!trimmedUsername) {
      setError('Username is required.');
      return;
    }
    if (trimmedUsername.length < 3) {
      setError('Username must be at least 3 characters.');
      return;
    }

    setLoading(true);
    let existingUserId;
    try {
      existingUserId = await getUserIdByUsername(trimmedUsername);
    } catch (lookupError) {
      setLoading(false);
      setError(lookupError.message);
      return;
    }

    if (existingUserId) {
      setLoading(false);
      setError('Username is already taken.');
      return;
    }

    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { username: trimmedUsername },
      },
    });
    setLoading(false);

    if (signUpError) {
      setError(signUpError.message);
      return;
    }

    if (data.session) {
      onAuth(data.session, data.user);
    } else {
      setMessage('Check your email to confirm your account, then sign in.');
      setMode('signin');
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    // Resolve username → email if the input has no '@'
    let resolvedEmail = loginId.trim();
    if (!resolvedEmail.includes('@')) {
      const foundEmail = await getEmailByUsername(resolvedEmail).catch(() => null);
      if (!foundEmail) {
        setLoading(false);
        setError('No account found with that username.');
        return;
      }
      resolvedEmail = foundEmail;
    }

    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email: resolvedEmail,
      password,
    });
    setLoading(false);

    if (signInError) {
      setError(signInError.message);
      return;
    }

    onAuth(data.session, data.user);
  };

  const handleSubmit = mode === 'signup' ? handleSignUp : handleSignIn;

  return (
    <div className={`auth-screen${onClose ? ' overlay' : ''}`} onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="auth-card">
        {onClose && (
          <button className="auth-close" onClick={onClose} aria-label="Close">✕</button>
        )}
        <div className="auth-header">
          <span className="auth-logo">✦</span>
          <h1 className="auth-title">Zapen</h1>
          <p className="auth-subtitle">
            {mode === 'signup' ? 'Create your account' : 'Welcome back'}
          </p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          {mode === 'signup' && (
            <div className="auth-field">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                placeholder="swift-lion"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
              />
            </div>
          )}

          {mode === 'signup' ? (
            <div className="auth-field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
          ) : (
            <div className="auth-field">
              <label htmlFor="loginId">Email or username</label>
              <input
                id="loginId"
                type="text"
                placeholder="you@example.com or swift-lion"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                autoComplete="username"
                required
              />
            </div>
          )}

          <div className="auth-field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              required
              minLength={6}
            />
          </div>

          {error && <p className="auth-error">{error}</p>}
          {message && <p className="auth-message">{message}</p>}

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading
              ? 'Please wait...'
              : mode === 'signup'
              ? 'Create Account'
              : 'Sign In'}
          </button>
        </form>

        <div className="auth-toggle">
          {mode === 'signup' ? (
            <>
              Already have an account?{' '}
              <button onClick={() => { setMode('signin'); setLoginId(''); setError(''); setMessage(''); }}>
                Sign in
              </button>
            </>
          ) : (
            <>
              Don't have an account?{' '}
              <button onClick={() => { setMode('signup'); setEmail(''); setError(''); setMessage(''); }}>
                Sign up
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthScreen;
